import { useState } from "react";
import { HashLoader } from "react-spinners";
import supabase from "../utils/supabase";
import { useNavigate } from "react-router-dom";
import { FcGoogle } from "react-icons/fc";

export default function SignUp() {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    fullName: "",
    emailPhone: "",
    password: "",
    confirmPassword: "",
  });

  const [errors, setErrors] = useState({});
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [progressMessage, setProgressMessage] = useState("");

  const handleInputChange = (e) => {
    const { id, value } = e.target;
    setFormData({ ...formData, [id]: value });
  };

  const pollEmailConfirmation = async (email) => {
    setProgressMessage("Waiting for email confirmation...");
    const MAX_RETRIES = 30; // Number of retries (30 retries = 2.5 minutes at 5s interval)
    const RETRY_INTERVAL = 5000; // Interval between retries in milliseconds (5 seconds)

    for (let i = 0; i < MAX_RETRIES; i++) {
      // Fetch the updated session
      const { data, error } = await supabase.auth.getSession();
      if (error) {
        console.error("Error fetching session:", error.message);
        return false;
      }

      const session = data.session;

      // Check if the email is confirmed
      if (session?.user?.email_confirmed_at) {
        setProgressMessage("Email confirmed. Completing signup...");
        return true;
      }

      // Wait for the next retry
      await new Promise((resolve) => setTimeout(resolve, RETRY_INTERVAL));
    }

    // If the email is not confirmed within the max retries, return false
    setProgressMessage("Email confirmation timed out.");
    return false;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setProgressMessage("Signing up...");
    const newErrors = {};

    // Basic validation logic
    if (!formData.fullName.trim()) newErrors.fullName = "Name is required.";
    if (!formData.emailPhone.trim())
      newErrors.emailPhone = "Email is required.";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.emailPhone.trim())) {
      newErrors.emailPhone = "Enter a valid email.";
    }
    if (!formData.password.trim()) newErrors.password = "Password is required.";
    if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = "Passwords do not match.";
    }

    setErrors(newErrors);

    if (Object.keys(newErrors).length === 0) {
      const { error } = await supabase.auth.signUp({
        email: formData.emailPhone,
        password: formData.password,
        options: {
          data: {
            full_name: formData.fullName,
          },
        },
      });

      if (error) {
        alert(error.message);
        setLoading(false);
      } else {
        const emailConfirmed = await pollEmailConfirmation(formData.emailPhone);

        if (emailConfirmed) {
          // Update the user's full name in the database
          const { error: updateError } = await supabase
            .from("users")
            .update({ name: formData.fullName })
            .eq("email", formData.emailPhone);

          if (updateError) {
            console.error(updateError);
            alert("Failed to update user information.");
          } else {
            navigate("/signup/preference");
          }
        } else {
          alert("Email confirmation failed. Please try again.");
        }
      }
    }
    setLoading(false);
  };

  const handleGoogleSignIn = async () => {
    setLoading(true);
    setProgressMessage("Connecting to Google...");
    const { error } = await supabase.auth.signInWithOAuth({
      provider: "google",
      options: {
        redirectTo: `${window.location.origin}/social`,
      },
    });
    if (error) {
      alert(error.message);
    }
    setLoading(false);
  };

  return (
    <div className="flex flex-col items-center justify-center h-screen bg-gradient-to-tr from-[#0072ff] to-[#003f7f]">
      <img src="/logo.png" alt="Logo" className="w-40 mx-auto" />

      {loading && (
        <div className="fixed top-0 left-0 z-50 w-full h-full flex flex-col items-center justify-center bg-black bg-opacity-50">
          <HashLoader color="#0072ff" size={50} />
          <p className="text-white mt-4">{progressMessage}</p>
        </div>
      )}
      <div className="bg-white/10 p-8 rounded-lg shadow-2xl w-4/5 max-w-sm mx-4 backdrop-blur-xl">
        <h2 className="text-2xl font-bold text-white text-center mb-6">
          Sign Up
        </h2>
        <form onSubmit={handleSubmit}>
          <div className="mb-4">
            <input
              type="text"
              id="fullName"
              value={formData.fullName}
              onChange={handleInputChange}
              className={`w-full p-3 bg-transparent text-white border-b ${
                errors.fullName ? "border-red-500" : "border-white/50"
              } focus:border-blue-300 focus:outline-none placeholder-gray-300`}
              placeholder="Name"
              required
            />
            {errors.fullName && (
              <p className="text-red-500 text-sm mt-1">{errors.fullName}</p>
            )}
          </div>
          <div className="mb-6">
            <input
              type="text"
              id="emailPhone"
              value={formData.emailPhone}
              onChange={handleInputChange}
              className={`w-full p-3 bg-transparent text-white border-b ${
                errors.emailPhone ? "border-red-500" : "border-white/50"
              } focus:border-blue-300 focus:outline-none placeholder-gray-300`}
              placeholder="Email"
              required
            />
            {errors.emailPhone && (
              <p className="text-red-500 text-sm mt-1">{errors.emailPhone}</p>
            )}
          </div>

          <div className="mb-6">
            <div className="relative">
              <input
                type={showPassword ? "text" : "password"}
                id="password"
                value={formData.password}
                onChange={handleInputChange}
                className={`w-full p-3 bg-transparent text-white border-b ${
                  errors.password ? "border-red-500" : "border-white/50"
                } focus:border-blue-300 focus:outline-none placeholder-gray-300`}
                placeholder="Password"
                required
              />
              <button
                type="button"
                className="absolute top-0 right-0 p-3 text-white/70"
                onClick={() => setShowPassword(!showPassword)}
              >
                {showPassword ? "Hide" : "Show"}
              </button>
            </div>
            {errors.password && (
              <p className="text-red-500 text-sm mt-1">{errors.password}</p>
            )}
          </div>

          <div className="mb-6">
            <input
              type="password"
              id="confirmPassword"
              value={formData.confirmPassword}
              onChange={handleInputChange}
              className={`w-full p-3 bg-transparent text-white border-b ${
                errors.confirmPassword ? "border-red-500" : "border-white/50"
              } focus:border-blue-300 focus:outline-none placeholder-gray-300`}
              placeholder="Confirm Password"
              required
            />
            {errors.confirmPassword && (
              <p className="text-red-500 text-sm mt-1">
                {errors.confirmPassword}
              </p>
            )}
          </div>

          <button
            type="submit"
            className="w-full bg-blue-500 text-white font-bold py-3 px-4 rounded-lg hover:bg-blue-600 transition-colors"
          >
            Sign Up
          </button>
        </form>

        <div className="flex items-center my-4">
          <div className="flex-1 border-t border-white/30"></div>
          <span className="px-3 text-white/70 text-sm">OR</span>
          <div className="flex-1 border-t border-white/30"></div>
        </div>

        <button
          onClick={handleGoogleSignIn}
          className="w-full bg-white text-gray-700 font-bold py-3 px-4 rounded-lg hover:bg-gray-100 transition-colors flex items-center justify-center gap-2"
        >
          <FcGoogle className="text-xl" />
          Sign up with Google
        </button>

        <p className="text-sm text-center text-gray-300 mt-4">
          Already have an account?{" "}
          <a
            href="/signin"
            className="text-blue-300 hover:underline transition-colors"
          >
            Sign In
          </a>
        </p>
      </div>
    </div>
  );
}
